import { loadScriptAsynchronously } from "client/Hooks";
import { IntercomRegion } from "client/Api/Intercom/intercomConfiguration";
import { never } from "client_legacy/ndaw/errors";
import { IntercomIntegrationModel, userConfiguration } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import {
	isNil,
	isNotNil
} from "client/Misc/utils";
import styles from "./Intercom.module.less";

let _wasInitialized = false;

export function initIntercom(): void {
	if (_wasInitialized) {
		return;
	}

	_wasInitialized = true;

	const { intercom } = userConfiguration();

	if (isNil(intercom)) {
		return;
	}

	initialize(intercom);
}

export async function shutdownIntercom(appId: string): Promise<void> {
	try {
		await loadWidgetScript(appId);

		(window as any)
			.Intercom("shutdown");

		await deleteIntercomCookies();
	} catch (e) {
		console.error("Failed to shut down Intercom", e);
	}
}

async function initialize(intercom: IntercomIntegrationModel): Promise<void> {
	try {
		const {
			appId,
			region,
			userJwt
		} = intercom;

		await loadWidgetScript(appId);

		(window as any)
			.Intercom(
				"boot",
				{
					app_id: appId,
					api_base: toIntercomApiBase(region),
					...(isNotNil(userJwt)
						? { intercom_user_jwt: userJwt }
						: {}
					)
				}
			);

			trackMessengerVisibility();
	} catch (e) {
		console.error("Failed to integrate with Intercom", e);
	}
}

function trackMessengerVisibility(): void {
	(window as any)
		.Intercom(
			"onShow",
			(): void => document.body
				.classList
				.add(styles.messengerIsOpen)
		);

	(window as any)
		.Intercom(
			"onHide",
			(): void => document.body
				.classList
				.remove(styles.messengerIsOpen)
		);
}

async function loadWidgetScript(appId: string): Promise<void> {
	await loadScriptAsynchronously(`https://widget.intercom.io/widget/${encodeURIComponent(appId)}`);
}

function toIntercomApiBase(region: IntercomRegion): string {
	switch (region) {
		case IntercomRegion.Us:
			return "https://api-iam.intercom.io";
		case IntercomRegion.Eu:
			return "https://api-iam.eu.intercom.io";
		case IntercomRegion.Au:
			return "https://api-iam.au.intercom.io";
		default:
			return never(region);
	}
}

async function deleteIntercomCookies(): Promise<void> {
	const { hostname } = window.location;

	const allCookies: readonly CookieListItem[] = await cookieStore.getAll();

	for (const cookie of allCookies) {
		if (isNil(cookie.name) || !cookie.name.startsWith(INTERCOM_COOKIE_PREFIX)) {
			continue;
		}

		await cookieStore.delete({
			name: cookie.name,
			domain: hostname,
			path: "/"
		});
	}
}

const INTERCOM_COOKIE_PREFIX = "intercom-";
